/**
 * Central query key factory for React Query.
 * Use these for cache keys and invalidation so keys stay consistent.
 */

import type { DocumentScope } from "@/services/docuseal";
import type { ManagedUserFilters } from "@/services/users";

export const queryKeys = {
  borrowers: (filters?: Record<string, unknown>, page?: number, pageSize?: number) =>
    ["borrowers", filters ?? {}, page ?? 1, pageSize ?? 20] as const,
  borrower: (id: number | string | null) =>
    id != null ? (["borrower", Number(id)] as const) : (["borrower", null] as const),
  /** Nested under `borrower(id)` so invalidating the deal also refetches its checklist. */
  borrowerChecklist: (id: number | null) =>
    id != null
      ? (["borrower", Number(id), "checklist"] as const)
      : (["borrower", null, "checklist"] as const),
  borrowerTransactions: (
    borrowerId: number | string | null,
    source: "statements" | "plaid" = "statements"
  ) =>
    borrowerId != null
      ? (["borrowerTransactions", Number(borrowerId), source] as const)
      : (["borrowerTransactions", null, source] as const),
  appStatusTransitions: () => ["appStatusTransitions"] as const,

  decisions: (params?: Record<string, unknown>) => ["decisions", params ?? {}] as const,
  decisionsByBorrower: (borrowerId: number | null) =>
    borrowerId != null
      ? (["decisionsByBorrower", borrowerId] as const)
      : (["decisionsByBorrower", null] as const),
  assessment: (id: number | null) =>
    id != null ? (["assessment", id] as const) : (["assessment", null] as const),
  assessmentByBorrower: (borrowerId: number | null) =>
    borrowerId != null
      ? (["assessmentByBorrower", borrowerId] as const)
      : (["assessmentByBorrower", null] as const),
  refinanceOfferByBorrower: (borrowerId: number | null) =>
    borrowerId != null
      ? (["refinanceOfferByBorrower", borrowerId] as const)
      : (["refinanceOfferByBorrower", null] as const),

  unicourtCases: (borrowerId: number | string | null) =>
    borrowerId != null
      ? (["unicourtCases", Number(borrowerId)] as const)
      : (["unicourtCases", null] as const),
  baselayerCheck: (borrowerId: number | null) => ["baselayerCheck", borrowerId] as const,
  baselayerWebPresence: (borrowerId: number | null) =>
    ["baselayerWebPresence", borrowerId] as const,
  datamerchNotes: (borrowerId: number | string | null) =>
    borrowerId != null
      ? (["datamerchNotes", Number(borrowerId)] as const)
      : (["datamerchNotes", null] as const),

  dashboardStats: () => ["dashboardStats"] as const,
  adminDashboardStats: () => ["adminDashboardStats"] as const,
  underwriters: () => ["underwriters"] as const,
  decisionsAnalytics: (days?: number) => ["decisionsAnalytics", days ?? 0] as const,
  managedUsers: (filters?: ManagedUserFilters) => ["managedUsers", filters ?? {}] as const,
  userCounts: () => ["userCounts"] as const,
  pendingInvitations: (filters?: ManagedUserFilters) =>
    ["pendingInvitations", filters ?? {}] as const,
  brokersAll: () => ["brokers"] as const,
  brokers: (search?: string, page?: number, isSteddyUser?: boolean | null) =>
    ["brokers", search ?? "", page ?? 1, isSteddyUser ?? null] as const,
  underwriterSearch: (search: string) => ["underwriters", "search", search] as const,

  statement: (id: number | null) =>
    id != null ? (["statement", id] as const) : (["statement", null] as const),

  funderDashboard: (days?: number) => ["funderDashboard", days ?? 30] as const,

  docusignForm: (borrowerId: number, assessmentId?: number | null) =>
    ["docusignForm", borrowerId, assessmentId ?? null] as const,

  contractAdminDocuments: (scope: DocumentScope["key"]) =>
    ["contractAdminDocuments", scope] as const,
  contractAdminDocumentsAll: () => ["contractAdminDocuments"] as const,
  contractFieldSeeds: (scope: DocumentScope["key"]) => ["contractFieldSeeds", scope] as const,
  contractRemoteTemplate: (scope: DocumentScope["key"], templateId: number) =>
    ["contractRemoteTemplate", scope, templateId] as const,
  contractBuilderToken: (scope: DocumentScope["key"], templateId: number) =>
    ["contractBuilderToken", scope, templateId] as const,
  contractForm: (
    borrowerId: number,
    contextId: number | null | undefined,
    documentId?: number | null
  ) => ["contractForm", borrowerId, contextId ?? null, documentId ?? null] as const,
  contractFormAll: () => ["contractForm"] as const,
  /** Lane-scoped by contract kind and jurisdiction. Null jurisdiction is the general lane. */
  contractStatus: (borrowerId: number, kind: string, jurisdiction?: string | null) =>
    ["contractStatus", borrowerId, kind, jurisdiction ?? null] as const,
  contractStatusAll: () => ["contractStatus"] as const,
  jurisdictions: () => ["jurisdictions"] as const,

  positionRunsList: (borrowerId: number) => ["positionRuns", "list", borrowerId] as const,
  positionRunsDetail: (id: number) => ["positionRuns", "detail", id] as const,

  analysisRunsList: (borrowerId: number) => ["analysisRuns", "list", borrowerId] as const,
  analysisRunsDetail: (id: number) => ["analysisRuns", "detail", id] as const,
  /** Under the runs' prefix, so whatever refetches the runs refetches a preview too. */
  analysisCorrectionsPreview: (borrowerId: number, source: string, items: unknown) =>
    ["analysisRuns", "correctionsPreview", borrowerId, source, items] as const,

  billingAccount: () => ["billingAccount"] as const,
  billingUsageMonths: (months?: number) => ["billingUsageMonths", months ?? 12] as const,
  billingUsageDays: (yearMonth: string) => ["billingUsageDays", yearMonth] as const,
  billingUsageEvents: (date: string) => ["billingUsageEvents", date] as const,
  billingInvoicesAll: () => ["billingInvoices"] as const,
  billingInvoices: (page?: number) => ["billingInvoices", page ?? 1] as const,

  /** GET agent/threads/: refetched on the agent socket's `threads.changed`. */
  agentThreads: () => ["agentThreads"] as const,
  /** GET agent/models/: the picker's models and this user's defaults. */
  agentModels: () => ["agentModels"] as const,
  /** GET agent/settings/: the user's default model. */
  agentSettings: () => ["agentSettings"] as const,
  /** GET agent/usage/: today's AI use as a percent of the daily limit (null when not shown). */
  agentUsage: () => ["agentUsage"] as const,
  /** GET borrowers/<id>/quick-look/: the assistant's Quick Look card. */
  applicationQuickLook: (id: number) => ["applicationQuickLook", id] as const,
  /** GET agent/sandbox/workspaces/?thread_id=: the thread's workspaces (its context's when bound). */
  agentWorkspacesAll: () => ["agentWorkspaces"] as const,
  agentWorkspaces: (threadId: number) => ["agentWorkspaces", threadId] as const,
  /** GET agent/sandbox/workspaces/<id>/files/: the Files panel; refetched on `workspace.changed`. */
  agentWorkspaceFilesAll: () => ["agentWorkspaceFiles"] as const,
  agentWorkspaceFiles: (workspaceId: string) => ["agentWorkspaceFiles", workspaceId] as const,
  /** GET agent/apps/approval/: the approval view's HTML, the same bytes for every frame. */
  agentApprovalView: () => ["agentApprovalView"] as const,
  /** GET agent/sandbox/dev/cells/<id>/: the dev inspector's view of one code cell. */
  sandboxDevCell: (toolCallId: string) => ["sandboxDevCell", toolCallId] as const,
} as const;
